import React, { useContext, useEffect, useState, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";

import { toast } from "react-toastify";

import { Paper, makeStyles } from "@material-ui/core";

import { AuthContext } from "../../context/Auth/AuthContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ContactProfile360Modal from "../ContactProfile360Modal";
import IaChatBriefingBanner from "../IaChatBriefingBanner";
import mergeContactPartial from "../../helpers/mergeContactPartial";
import MessageInput from "../MessageInputCustom/";
import MessagesList from "../MessagesList";
import TicketActionButtons from "../TicketActionButtonsCustom";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import { TagsContainer } from "../TagsContainer";
import { CHAT } from "../../theme/redesign";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
  },

  /** Área chat: clic cabecera abre perfil 360 en panel derecho (`ContactProfile360Modal` Drawer, 0287). */
  mainWrapper: {
    flex: 1,
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
    borderLeft: "0",
    border: "none",
    backgroundColor: CHAT.paneBg(theme),
    backgroundImage: CHAT.panePattern(theme),
    backgroundRepeat: "repeat",
    backgroundSize: "340px 340px",
  },
}));

const Ticket = ({ ticketId: ticketIdProp, embedded = false, onClose }) => {
  const params = useParams();
  const ticketId = ticketIdProp || params.ticketId;
  const history = useHistory();
  const classes = useStyles();

  const { user } = useContext(AuthContext);

  const [profile360Open, setProfile360Open] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const isMounted = useRef(true);
  const ticketRef = useRef(ticket); // ✅ REFERENCIA PARA ACCEDER AL TICKET ACTUAL EN LISTENERS

  const socketManager = useContext(SocketContext);
  
  // ✅ FIX 05/12/2025: UN SOLO useEffect controla isMounted cuando el componente se monta/desmonta
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // ✅ Sin dependencias = solo al montar/desmontar el componente
  
  // ✅ ACTUALIZAR REFERENCIA CUANDO EL TICKET CAMBIA
  useEffect(() => {
    ticketRef.current = ticket;
  }, [ticket]);

  useEffect(() => {
    // ✅ FIX 05/12/2025: NO modificar isMounted aquí, solo leerlo
    // 0297 Fase A: sin demora artificial previa a GET /tickets/u/ (antes 500 ms encadenaba con MessagesList).
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/u/" + ticketId);
          const { queueId } = data;
          const { queues, profile } = user;

          const queueAllowed = queues.find((q) => q.id === queueId);
          if (queueAllowed === undefined && profile !== "admin") {
            toast.error("No tienes permiso para abrir este ticket");
            if (!embedded) history.push("/tickets");
            return;
          }

          if (isMounted.current) {
            setContact(data.contact);
            setTicket(data);
            setLoading(false);
          }

          // 0297 Fase B.2: marcar leído sin await — no compite en la cola con GET /messages tras el paint.
          api
            .put(`/tickets/${data.id}/read`)
            .catch((error) => {
              console.warn("⚠️ ERROR AL MARCAR TICKET COMO LEÍDO:", error);
            });
        } catch (err) {
          if (isMounted.current) {
            setLoading(false);
            if (err?.response?.data?.error === "ERR_NO_TICKET_FOUND") {
              if (embedded) onClose?.();
              else history.replace("/tickets");
              return;
            }
            toastError(err);
          }
        }
      };
      fetchTicket();
    }, 0);
    return () => {
      clearTimeout(delayDebounceFn);
      // ✅ FIX 05/12/2025: NO modificar isMounted aquí, solo leerlo
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, user, history, embedded]);

  useEffect(() => {
    // ✅ FIX CRÍTICO 08/01/2026: Remover log para evitar spam
    // console.log(`[Ticket Component] useEffect Socket.IO re-running for ticketId: ${ticketId}`);
    
    // ✅ FIX 05/12/2025: NO modificar isMounted aquí, solo leerlo
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ NOTA: ticketId de la URL es UUID, pero ticket.id del estado es número
    // Usamos ticketRef para acceder al ticket actual en los listeners

    // ✅ CORRECCIÓN #3: Función para suscribirse al canal del ticket
    const subscribeToTicket = () => {
      const currentTicketNumericId = ticketRef.current?.id;
      if (currentTicketNumericId) {
        socket.emit("joinChatBox", `${currentTicketNumericId}`);
      }
    };

    // ✅ FIX CRÍTICO 08/01/2026: Guardar referencias a funciones para poder limpiarlas correctamente
    const handleReady = () => {
      subscribeToTicket();
    };

    const handleConnect = () => {
      // Re-suscribirse después de una reconexión
      subscribeToTicket();
    };

    // ✅ CORRECCIÓN #3: Suscribirse cuando el socket está ready
    socket.on("ready", handleReady);

    // ✅ CORRECCIÓN #3: Re-suscribirse cuando el socket se reconecta
    socket.on("connect", handleConnect);

    // ✅ CORRECCIÓN CRÍTICA: Escuchar SOLO eventos del ticket actual
    // El problema es que estábamos escuchando el canal global company-${companyId}-ticket
    // que recibe eventos de TODOS los tickets. Necesitamos filtrar por el ID del ticket actual.
    const handleTicketUpdate = (data) => {
      if (!isMounted.current) return;
      
      const eventTicketId = data.ticket?.id || data.ticketId;
      const currentTicketNumericId = ticketRef.current?.id;
      const sameTicket =
        eventTicketId != null &&
        currentTicketNumericId != null &&
        Number(eventTicketId) === Number(currentTicketNumericId);

      if (currentTicketNumericId && eventTicketId && !sameTicket) {
        return;
      }
      
      if (data.action === "update" && data.ticket) {
        if (sameTicket) {
          const previousStatus = ticketRef.current?.status;
          
          // ✅ CRÍTICO: Preservar user y contact existentes si no vienen en el evento
          // Esto previene que desaparezca la cabecera del contacto cuando se actualizan etiquetas
          setTicket((prevTicket) => ({
            ...prevTicket, // ✅ Preservar propiedades existentes
            ...data.ticket, // ✅ Actualizar con datos nuevos
            // ✅ Si el evento no incluye user o contact, preservar los existentes
            user: data.ticket.user || prevTicket.user,
            contact: data.ticket.contact
              ? mergeContactPartial(prevTicket.contact, data.ticket.contact)
              : prevTicket.contact,
            contactId: data.ticket.contactId || prevTicket.contactId,
            userId: data.ticket.userId || prevTicket.userId
          }));
          
          // ✅ Si el ticket se cerró, mostrar mensaje informativo
          if (data.ticket.status === "closed" && previousStatus !== "closed") {
            toast.success("Ticket cerrado correctamente");
          }
        }
        // Si el ticket aún no está cargado pero recibimos un evento, actualizar de todas formas
        // Esto maneja el caso donde el evento llega antes de que el ticket se cargue completamente
        else if (eventTicketId && !currentTicketNumericId && data.ticket?.id) {
          setTicket({ ...data.ticket }); // Crear nuevo objeto
        } else {
          // Ignorar silenciosamente updates no aplicables
        }
      }

      // ✅ CORRECCIÓN: Solo procesar DELETE si realmente se eliminó el ticket (no cuando se cierra)
      // El backend emite DELETE cuando cambia de status, pero el UPDATE ya maneja el cierre
      if (data.action === "delete") {
        if (!sameTicket) return;
        const incomingStatus = data.ticket?.status;
        // DELETE también se emite al pasar de "en espera" a "en atención".
        // Eso no cierra ni elimina el chat que el agente acaba de abrir.
        if (incomingStatus && incomingStatus !== "closed") {
          return;
        }
        if (incomingStatus === "closed") {
          setTicket((prevTicket) => ({
            ...prevTicket,
            ...data.ticket,
            status: "closed"
          }));
          return;
        }
        if (!data.ticket && !embedded) {
          history.push("/tickets");
        }
      }
    };
    
    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);

    // ✅ FIX CRÍTICO 08/01/2026: Guardar referencia a función para poder limpiarla correctamente
    const handleContactUpdate = (data) => {
      if (!isMounted.current) return;
      
      if (data.action === "update") {
        setContact((prevState) => {
          // ✅ FIX: Comparar con ticket.contactId también, ya que el contacto puede no tener id inicialmente
          const eventContactId = data.contact?.id;
          const matchesPrevState = prevState.id === eventContactId;
          const matchesTicket = ticket?.contactId === eventContactId;
          
          if (matchesPrevState || matchesTicket || (!prevState.id && eventContactId)) {
            const merged = mergeContactPartial(prevState, data.contact);
            return {
              ...merged,
              updatedAt: data.contact.updatedAt || new Date().toISOString()
            };
          } else {
          }
          return prevState;
        });
      }
    };

    socket.on(`company-${companyId}-contact`, handleContactUpdate);

    // ✅ FIX CRÍTICO 08/01/2026: Guardar referencia a función para poder limpiarla correctamente
    const handleAppMessage = (data) => {
      if (!isMounted.current) return;
      
      // ✅ Validar que el mensaje sea para el ticket actual
      const messageTicketId = data.message?.ticketId || data.ticket?.id;
      const currentTicketNumericId = ticketRef.current?.id;
      
      // FIX: Comparar como número para evitar string "1305" !== number 1305
      const idsMatch = messageTicketId && currentTicketNumericId && 
        (Number(messageTicketId) === Number(currentTicketNumericId) || String(messageTicketId) === String(currentTicketNumericId));
      if (idsMatch && data.action === "create" && data.ticket) {
        setTicket((prevState) => ({
          ...prevState,
          lastMessage: data.message?.body || data.ticket.lastMessage,
          updatedAt: data.ticket.updatedAt || new Date()
        }));
      }
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);

    // ✅ FIX CRÍTICO 08/01/2026: Guardar referencia a función para poder limpiarla correctamente
    const handlePresence = (data) => {
      if (!isMounted.current) return;
      
      // ✅ CORRECCIÓN: Usar el ID numérico del ticket desde el estado local
      const currentTicketNumericId = ticketRef.current?.id;

      if (data.ticketId && currentTicketNumericId && data.ticketId === currentTicketNumericId) {
        setTicket((prevState) => ({
          ...prevState,
          presence: data.presence
        }));
        
        // ✅ Auto-limpiar "composing" después de 10 segundos
        if (data.presence === "composing") {
          setTimeout(() => {
            if (!isMounted.current) return;
            setTicket((prevState) => {
              // Asegurar que el ticket actual sigue siendo el mismo antes de limpiar
              if (prevState.id === currentTicketNumericId && prevState.presence === "composing") {
                return { ...prevState, presence: "available" };
              }
              return prevState;
            });
          }, 10000);
        }
      }
    };

    socket.on(`company-${companyId}-presence`, handlePresence);

    return () => {
      // ✅ FIX CRÍTICO 08/01/2026: Limpiar TODOS los listeners usando las referencias a las funciones
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
        socket.off(`company-${companyId}-appMessage`, handleAppMessage);
        socket.off(`company-${companyId}-contact`, handleContactUpdate);
        socket.off(`company-${companyId}-presence`, handlePresence);
        socket.off("ready", handleReady);
        socket.off("connect", handleConnect);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]); // ✅ FIX CRÍTICO 08/01/2026: Solo re-ejecutar cuando cambia ticketId, no cuando cambia socketManager

  const openProfile360 = () => {
    if (contact?.id) setProfile360Open(true);
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={openProfile360}
        />
      );
    }
  };

  const renderMessagesList = () => {
    // ✅ CORRECCIÓN: Solo renderizar MessagesList cuando el ticket esté completamente cargado
    // Esto previene que el componente se monte con ticketId undefined
    if (!ticket?.id) {
      return null;
    }
    
    return (
      <>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
        ></MessagesList>
        <IaChatBriefingBanner contact={contact} ticket={ticket} onOpenProfile360={openProfile360} />
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          ticket={ticket} // ✅ NUEVO: Pasar objeto ticket completo para detectar IA
        />
      </>
    );
  };

  return (
    <div className={classes.root} id="ticket-panel-root">
      <Paper
        elevation={0}
        className={classes.mainWrapper}
      >
        <TicketHeader loading={loading} onBack={embedded ? onClose : undefined}>
          {renderTicketInfo()}
          <TicketActionButtons ticket={ticket} embedded={embedded} onOpenContactDetails={openProfile360} />
        </TicketHeader>
        {ticket?.id && !loading && (
          <TagsContainer ticket={ticket} />
        )}
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <ContactProfile360Modal
        open={profile360Open}
        onClose={() => setProfile360Open(false)}
        contactId={contact?.id}
        onContactUpdated={async () => {
          try {
            const { data } = await api.get("/tickets/u/" + ticketId);
            if (isMounted.current) setContact(data.contact || {});
          } catch (_) {
            /* noop */
          }
        }}
      />
    </div>
  );
};

export default Ticket;
